import { VOICE_CONTINUE_SENTINEL, type PageSnapshot } from "@ai-guidance/shared";
import type { SdkAction } from "../commerce/wire";
import { domSettled } from "../settle";
import { currentPagePath } from "../page";
import type { Presence } from "../presence";
import { DEFAULT_VOICE_NAME, type VoiceName } from "./voiceName";
import type { VoiceProvider, VoiceSessionInfo, VoiceState } from "./types";

/**
 * Vezi `docs/plan-voice-variants.md` → „VERDICTUL A/B-ULUI": Gemini Live a
 * pierdut A/B-ul (9 aug 2026), iar opțiunea de test a plecat din UI. Un singur
 * membru rămas deliberat, nu string simplu - deci reactivarea (re-conectarea
 * la `gemini.ts`, dormant dar netăiat) e un `| "gemini"` aici, nu o rescriere.
 */
export type VoiceVariant = "elevenlabs";

/**
 * Conversația vocală, așa cum o trăiește vizitatorul pe situl clientului.
 *
 * Codul ăsta stă în `sdk.js` (deci pe fiecare pagină), dar e mic: tot ce e greu
 * - SDK-ul providerului - vine din `voice.js`, încărcat abia la primul click pe
 * microfon. Aici rămâne doar orchestrarea:
 *
 *   sesiune la backendul nostru → chunkul de provider → orb + waveform pe stările
 *   reale → spotlight din acțiunile de ghidare
 *
 * Legătura cu ghidarea e ce face modul ăsta să fie al NOSTRU și nu un chatbot
 * vocal oarecare: în timp ce agentul vorbește, aceleași acțiuni care aprind
 * spotlight-ul în chatul scris se execută pe pagina reală. „Uite unde" chiar
 * arată unde.
 */

export interface VoiceControllerOptions {
  appId: string;
  publicKey: string;
  apiBase: string;
  sessionId: string;
  presence: Presence;
  /**
   * Execută acțiunile de ghidare - același `ActionExecutor` ca la chatul scris.
   * Așteptat: `waitForElement` chiar așteaptă, iar continuarea de după o
   * navigare nu are voie să pornească înainte ca execuția să se fi terminat.
   */
  runActions: (actions: SdkAction[]) => void | Promise<void>;
  /**
   * Capturează ce vede vizitatorul ACUM - același snapshot ca la chatul scris,
   * cu efectul lateral esențial: leagă harta `idx → nod DOM` de executor, ca
   * acțiunile turei următoare să țintească exact snapshotul trimis. Fără
   * snapshot (pagină exclusă), întoarce `undefined` pe câmpul respectiv.
   */
  /**
   * Poate fi asincron: pe un magazin, captura mai atașează și starea coșului
   * (`snapshot.cart`), citită din API-ul platformei. Plafonată în SDK, ca
   * urcarea ecranului să nu întârzie tura - vezi `CommerceRuntime.snapshotExtras`.
   */
  capturePage: () =>
    | { path: string; title: string; snapshot?: PageSnapshot }
    | Promise<{ path: string; title: string; snapshot?: PageSnapshot }>;
  /** Replica, pe măsură ce apare - o arătăm sub orb. */
  onCaption: (role: "user" | "agent", text: string) => void;
  onError: (message: string) => void;
  onStateChange?: (state: VoiceState) => void;
  /**
   * Reluarea automată de după o navigare a fost blocată de browser (gest de
   * utilizator cerut pentru microfon), dar sesiunea de server trăiește și
   * următorul tap pe microfon o continuă. Suprafața arată nota - altfel
   * vizitatorul vede doar „conversația s-a închis singură".
   */
  onResumeBlocked?: () => void;
  /**
   * Minutele de voce s-au terminat și conversația s-a închis DUPĂ rămas-bunul
   * rostit (F-06). Suprafața arată nota „continuăm în scris" - transcriptul e
   * deja în chat, deci nu se pierde nimic.
   */
  onQuotaEnded?: () => void;
  /**
   * Varianta cu care PORNEȘTE pagina. Un singur membru posibil azi - vezi
   * `VoiceVariant`.
   */
  variant?: VoiceVariant;
  /**
   * Vocea conversațiilor pornite din această pagină (`?voice=` sau
   * `branding.voice`, traduse și validate în `sdk.ts` → `voiceName.ts`).
   * Absentă = vocea agentului.
   */
  voiceName?: VoiceName;
}

/** Cât de des întrebăm backendul de acțiuni noi și de cost. */
const POLL_MS = 500;

/**
 * Acțiunile care MUTĂ vizitatorul pe altă pagină. Oglindă a setului cu același
 * nume din `services/agentStream.ts` - acolo decide dacă tura are voie să fie
 * mută, aici dacă saltul trebuie să aștepte vocea.
 */
const PAGE_CHANGING_ACTIONS = new Set(["navigate", "open_search", "open_checkout"]);

/**
 * Cât așteptăm, cel mult, sfârșitul replicii înainte să navigăm oricum. Plasa de
 * siguranță pentru cazul în care semnalul de stare al providerului nu mai vine
 * deloc (conexiune moartă, un eveniment pierdut): vizitatorul care a cerut o
 * pagină trebuie să ajungă pe ea, chiar dacă asta taie ultimele cuvinte.
 */
const SPEECH_END_TIMEOUT_MS = 15_000;

/**
 * Cât rabdă ieșirea din „speaking" înainte să conteze ca sfârșit de replică.
 * Pe o replică lungă, redarea poate avea un gol scurt între segmente (sinteza
 * încă lucrează la propoziția următoare), iar `onModeChange` trece o clipă pe
 * „listening" și înapoi. Fără răgazul ăsta, golul elibera saltul amânat exact
 * la jumătatea replicii (raportat 12 aug 2026: redirectul către pagina de
 * retururi a tăiat explicația în două). Revenirea pe „speaking" în fereastra
 * asta anulează semnalul; „idle" (stop, deconectare) rămâne instant.
 */
const SPEECH_END_GRACE_MS = 700;

/**
 * Semnul lăsat în `sessionStorage` chiar înainte ca o navigare vocală să
 * descarce documentul. E singurul lucru care traversează reload-ul: obiectul
 * `VoiceController`, conexiunea la ElevenLabs și tot ce ține de audio mor odată
 * cu pagina. Sesiunea de la NOI, în schimb, trăiește mai departe pe server -
 * deci tot ce trebuie cărat dincolo e tokenul ei.
 */
const RESUME_KEY = "aig_voice_resume";

/**
 * Cât de vechi poate fi markerul ca să mai însemne ceva. Un reload cauzat de
 * agent e o chestiune de secunde; peste un minut, ce găsim e mai degrabă un tab
 * lăsat deschis sau un back/forward - iar a porni microfonul nesolicitat, la
 * minute după ce omul a plecat din conversație, ar fi o surpriză neplăcută.
 */
const RESUME_TTL_MS = 60_000;

/**
 * Sub plafonul ăsta, o a doua cădere de conexiune nu se mai reconectează - e
 * o cauză persistentă (rețea, cota providerului), iar o buclă de reconectări
 * ar ține microfonul aprins la nesfârșit pe o conversație care nu poate trăi.
 */
const AUTO_RECONNECT_COOLDOWN_MS = 60_000;

/**
 * Câte poll-uri consecutive cu 404 înseamnă „sesiunea chiar nu mai există"
 * (restart de backend), nu un sughiț de rețea. La 500ms per bătaie, 6 = ~3s.
 */
const SESSION_GONE_POLLS = 6;

/**
 * CINE a provocat saltul care descarcă documentul - decide ce se întâmplă pe
 * pagina nouă după reconectare:
 *   - `agent`: o acțiune a agentului (navigate/open_search/open_checkout) în
 *     mijlocul turei → „timpul doi": ecranul nou pe sesiune + sentinelul, iar
 *     backendul reia întrebarea originală peste pagina nouă;
 *   - `human`: vizitatorul a dat click el pe un link/categorie/paginare →
 *     reconectare tăcută, agentul doar ascultă din nou. FĂRĂ sentinel: altfel
 *     ultima întrebare se rejoacă pe fiecare pagină pe care omul o deschide
 *     singur, iar dacă răspunsul navighează și el, bucla nu se mai oprește
 *     („de ce tot repeți?" - Nordicshops, 21 sep 2026, F-01).
 */
export type ResumeReason = "agent" | "human";

interface ResumeMarker {
  token: string;
  at: number;
  reason: ResumeReason;
}

/**
 * Citește markerul și îl ȘTERGE, o singură dată, indiferent de rezultat: o
 * reconectare ratată nu are voie să se reîncerce singură la fiecare pagină.
 */
function takeVoiceResume(): ResumeMarker | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RESUME_KEY);
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    // Storage blocat (Safari în mod privat, cookie-uri terțe): nu e nimic de
    // reluat, iar vocea rămâne pornibilă manual.
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ResumeMarker>;
    if (typeof parsed?.token !== "string" || typeof parsed?.at !== "number") return null;
    if (Date.now() - parsed.at > RESUME_TTL_MS) return null;
    // Marker fără motiv = scris de un SDK dinainte de câmp (cache-ul unei
    // pagini vechi): pe atunci orice marker însemna salt de agent.
    return { token: parsed.token, at: parsed.at, reason: parsed.reason === "human" ? "human" : "agent" };
  } catch {
    return null;
  }
}

export class VoiceController {
  private provider: VoiceProvider | null = null;
  private token: string | null = null;
  private poll = 0;
  private raf = 0;
  private state: VoiceState = "idle";
  /** Calea ultimului snapshot urcat - navigarea se detectează comparând cu ea. */
  private pushedPath = "";
  /** O reluare de tură e în curs - vezi `resumeAfterNavigation`. */
  private resuming = false;
  /**
   * Sesiunea de server care așteaptă un GEST ca să fie reluată: reconectarea
   * automată de după navigare a fost blocată (Safari cere gest de utilizator
   * pentru microfon), dar sesiunea trăiește (TTL 30 min pe server). Următorul
   * tap pe microfon o reia pe EA, nu deschide alta - vezi `doResume`/`doStart`.
   */
  private pendingResume: { token: string; reason: ResumeReason } | null = null;
  /** Ascultătorul de `pagehide` - navigarea făcută de om scrie și ea markerul. */
  private onPageHide?: () => void;
  /** Poll-uri consecutive cu 404 - vezi `SESSION_GONE_POLLS`. */
  private gonePolls = 0;
  /**
   * Loturile de acțiuni deja executate (id-uri idempotente de pe poll).
   * Sesiunea rehidratată după un restart de backend poate relivra outbox-ul -
   * ce s-a executat o dată nu se mai execută. Trăiește cât controllerul, adică
   * cât pagina; peste un reload, `navigate`-ul dublat e oricum no-op pe
   * pagina pe care a dus deja.
   */
  private executedBatches = new Set<string>();
  /**
   * Varianta pornită, ÎN MEMORIE - vezi comentariul de pe `VoiceVariant`.
   * Un singur membru posibil azi; câmpul rămâne (nu un literal fix) fiindcă
   * cererea către backend îl consumă tipat, ca la reactivare.
   */
  private variant: VoiceVariant;
  /**
   * Vocea cerută pentru conversațiile pornite de pe pagina asta. Vine gata
   * validată din `sdk.ts` (`?voice=` lipicios per tab, sau `branding.voice`); controlerul nu
   * citește niciodată singur URL-ul sau storage-ul.
   *
   * Citită la FIECARE pornire, nu înghețată în constructor: din 12 aug 2026
   * alegerea se poate schimba în mijlocul tabului (comanda `/voice-2` scrisă în
   * card), iar `sdk.ts` reîmprospătează opțiunea înainte de `start()`. La fel ca
   * `sessionId`, care se schimbă la resetul conversației.
   */
  private get voiceName(): VoiceName {
    return this.opts.voiceName ?? DEFAULT_VOICE_NAME;
  }
  /**
   * Tura CURENTĂ are replică rostită: providerul ne-a dat un transcript de agent
   * cu text real (spațiul-placeholder al turei mute nu se pune - vezi
   * `agentStream.ts`). E singurul semn pe care îl avem că modelul a ales să
   * vorbească ÎNAINTE să navigheze.
   */
  private spokeThisTurn = false;
  /** Am apucat să vedem starea „speaking" în tura curentă (deci redarea a pornit). */
  private speakingSeen = false;
  /** Cine așteaptă sfârșitul replicii - vezi `awaitSpeechEnd`. */
  private speechEndWaiters = new Set<() => void>();
  /** Răgazul care separă golul dintre segmente de sfârșitul real al replicii. */
  private speechEndGrace = 0;

  constructor(private readonly opts: VoiceControllerOptions) {
    this.variant = opts.variant ?? "elevenlabs";
  }

  get active(): boolean {
    return this.provider !== null;
  }

  /**
   * Pornirea (sau reluarea) aflată în zbor chiar acum. `active` devine adevărat
   * abia la finalul lui `attach`, după mai multe `await`-uri lungi (promptul de
   * microfon, mint-ul sesiunii, încărcarea chunkului) - pe telefon, secunde
   * întregi. Un al doilea tap în fereastra aia trecea de `if (this.provider)`
   * și pornea O A DOUA conversație ElevenLabs completă: două stream-uri audio,
   * „două voci în paralel" (raportat pe mobil, 11 aug 2026). Orice intrare
   * (start, resume, stop) așteaptă întâi zborul curent.
   */
  private inFlight: Promise<unknown> | null = null;

  async start(): Promise<void> {
    // În chiar gestul de tap, înainte de orice await: browserele lasă audio-ul
    // să pornească doar dintr-o interacțiune, iar clipul de salut vine abia
    // după răspunsul backendului. Un AudioContext deschis AICI rămâne deschis.
    this.unlockClipAudio();
    // Bucla stă INLINE, nu într-un helper async: un `await` pe un helper
    // suspendă un microtask chiar și când nu e nimic în zbor, iar în golul ăla
    // un al doilea apel concurent trece de verificare înainte ca primul să
    // apuce să pună lacătul. De la condiție la `this.inFlight = ...` de mai
    // jos nu are voie să existe niciun punct de suspendare.
    while (this.inFlight) await this.inFlight.catch(() => {});
    if (this.provider) return;
    const run = this.doStart();
    this.inFlight = run.catch(() => {});
    try {
      await run;
    } finally {
      this.inFlight = null;
    }
  }

  /**
   * Rulează o tranziție de conexiune sub același lacăt ca `start()` - vezi
   * comentariul de-acolo pentru de ce bucla e inline și nu un helper cu await
   * înainte de a pune lacătul.
   */
  private async runExclusive(work: () => Promise<void>): Promise<void> {
    while (this.inFlight) await this.inFlight.catch(() => {});
    const run = work();
    this.inFlight = run.catch(() => {});
    try {
      await run;
    } finally {
      this.inFlight = null;
    }
  }

  private async doStart(): Promise<void> {
    const { appId, publicKey, apiBase } = this.opts;

    // Microfonul REFUZAT se verifică întâi, fiindcă e singurul eșec pe care
    // vizitatorul îl poate repara - și e invizibil altfel: odată blocat pentru
    // un site, `getUserMedia` respinge instant, fără să mai afișeze vreo cerere.
    // Fără verificarea asta, tot ce vede omul e un buton care nu face nimic.
    // Permissions API lipsește pe unele browsere; acolo mergem mai departe și
    // lăsăm `getUserMedia` să decidă, ca până acum.
    if (typeof navigator !== "undefined" && navigator.permissions?.query) {
      try {
        const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
        if (status.state === "denied") throw new Error("mic_denied");
      } catch (err) {
        if (err instanceof Error && err.message === "mic_denied") throw err;
        // Interogare nesuportată pentru „microphone" - nu e un motiv să nu încercăm.
      }
    }

    // Pe un origin NOU permisiunea nu e nici „granted", nici „denied": promptul
    // apare abia când cineva cere efectiv microfonul. Îl cerem NOI, aici, și nu
    // lăsăm SDK-ul providerului să-l ceară din adâncul lui (`Conversation
    // .startSession` face getUserMedia intern) - două motive, ambele văzute pe
    // 10 aug 2026 pe staging:
    //   1. eroarea lor e ÎMPACHETATĂ, deci `name` nu mai e „NotAllowedError" și
    //      vizitatorul primea mesajul generic în loc de instrucțiunile de
    //      deblocare;
    //   2. la refuz mintam degeaba o sesiune și urcam ecranul, pentru o
    //      conversație care nu putea porni.
    // Stream-ul se închide imediat: ne trebuia permisiunea, nu audio. Providerul
    // își cere al lui o clipă mai târziu, fără prompt - permisiunea e deja dată.
    // Doar pornirea MANUALĂ trece pe-aici; `resumeAfterReload` intră direct în
    // `attach`, unde nu se cere nimic în plus.
    if (typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia) {
      // Eroarea nativă (NotAllowedError / NotFoundError) se propagă neatinsă -
      // `voiceErrorMessage` din `sdk.ts` are deja ramurile potrivite pentru ea.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    }

    // O sesiune își așteaptă gestul (reluarea automată de după navigare a fost
    // blocată - vezi `doResume`)? Tap-ul care ne-a adus aici E gestul: reluăm
    // conversația EXACT de unde era, nu deschidem alta peste ea. Consumat
    // înainte de încercare, ca un eșec să nu se reîncerce la nesfârșit; dacă
    // între timp sesiunea a expirat pe server, cădem pe pornirea curată de jos.
    const pending = this.pendingResume;
    this.pendingResume = null;
    if (pending) {
      try {
        const session = await this.mintReconnect(pending.token);
        await this.attach({ ...session, voiceToken: pending.token }, { suppressGreeting: true });
        void this.afterResume(pending.reason);
        return;
      } catch (err) {
        console.info("[ai-guidance] voice: pending session gone, starting fresh", err);
      }
    }

    const response = await fetch(`${apiBase}/v1/apps/${appId}/voice/session`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-public-key": publicKey },
      body: JSON.stringify({
        provider: this.variant,
        sessionId: this.opts.sessionId,
        // Aceeași identitate de pagină ca pe calea text: pathname + hash.
        // `location.pathname` gol ar face toate paginile unui SPA cu
        // hash-routing să arate identic pentru agent.
        path: currentPagePath(),
        // Cheia de voce, validată pe server contra allow-list-ului variantei.
        // „agent" (ElevenLabs) e absența alegerii, nu o voce: nu pleacă deloc,
        // iar agentul își păstrează vocea configurată.
        ...(this.voiceName === "agent" ? {} : { voice: this.voiceName }),
      }),
    });
    if (!response.ok) {
      // 503 = cheile providerului nu sunt puse pe acest mediu. E starea normală
      // oriunde nu testăm vocea, deci merită un mesaj propriu, nu „a picat ceva".
      // 429 = de regulă plafonul de conversații SIMULTANE al contului ElevenLabs
      // (Starter = 6) sau plafonul per app pe minut - stare temporară de trafic,
      // nu o defecțiune: butonul rămâne funcțional pentru un retry manual, dar
      // mesajul trebuie să spună asta (vezi `voiceErrorMessage` din `sdk.ts`).
      // Excepția: 429 cu `voice_quota_exhausted` = cota LUNARĂ de minute a
      // contului s-a terminat (25 oct 2026) - nu e „ocupat", nu are rost retry;
      // același mesaj ca la 403 (minutele vizitatorului, F-06).
      // 403 = minutele de voce ale vizitatorului s-au terminat (F-06) - nu o
      // defecțiune: conversația continuă în scris, iar mesajul spune exact asta.
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(
        response.status === 503
          ? "voice_not_configured"
          : response.status === 429
            ? body?.error === "voice_quota_exhausted"
              ? "voice_quota_exhausted"
              : "voice_busy"
            : response.status === 403
              ? "voice_quota_exhausted"
              : `voice_session_failed_${response.status}`,
      );
    }
    const session = (await response.json()) as VoiceSessionResponse;
    this.token = session.voiceToken;

    // Salutul preînregistrat pornește ACUM, în paralel cu tot ce urmează
    // (chunkul de voce, handshake-ul, microfonul) - vizitatorul aude ceva la
    // sub o secundă de la tap, nu după ce providerul a sintetizat textul.
    const clip = session.greetingClipHash;
    if (clip) void this.playGreetingClip(clip);

    // Ecranul pleacă pe sesiune ÎNAINTE să se deschidă urechea: prima replică
    // a vizitatorului poate veni imediat, iar tura ei trebuie să vadă pagina.
    await this.pushPage();

    // Cu clip, conversația se deschide MUTĂ (același mecanism ca la
    // reconectare): salutul s-a auzit deja din browser.
    await this.attach(session, clip ? { suppressGreeting: true } : undefined);
  }

  // ── Salutul preînregistrat ────────────────────────────────────────────────
  private clipAudio: AudioContext | null = null;
  private clipAnalyser: AnalyserNode | null = null;
  private clipSource: AudioBufferSourceNode | null = null;
  /** Clipul se aude chiar acum - orbul arată „vorbește", nivelul vine din analyser. */
  private clipPlaying = false;
  private clipLevelBuf: Uint8Array<ArrayBuffer> | null = null;

  private unlockClipAudio(): void {
    if (this.clipAudio) {
      if (this.clipAudio.state === "suspended") void this.clipAudio.resume().catch(() => {});
      return;
    }
    const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    if (!Ctx) return;
    try {
      this.clipAudio = new Ctx();
      if (this.clipAudio.state === "suspended") void this.clipAudio.resume().catch(() => {});
    } catch {
      this.clipAudio = null;
    }
  }

  private async playGreetingClip(hash: string): Promise<void> {
    const ctx = this.clipAudio;
    if (!ctx) return;
    const { appId, publicKey, apiBase } = this.opts;
    try {
      const response = await fetch(`${apiBase}/v1/apps/${appId}/voice/greeting/${hash}.mp3`, {
        headers: { "x-public-key": publicKey },
      });
      if (!response.ok) return;
      const buffer = await ctx.decodeAudioData(await response.arrayBuffer());
      // Între timp vizitatorul a apăsat Stop, sau a și vorbit: salutul nu mai
      // are rost. `token` cade la stop; `clipPlaying` e false până aici.
      if (!this.token || this.clipSource) return;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      this.clipAnalyser = analyser;
      this.clipLevelBuf = new Uint8Array(analyser.fftSize);
      this.clipSource = source;
      this.clipPlaying = true;
      source.onended = () => {
        if (this.clipSource !== source) return;
        this.stopGreetingClip(false);
      };
      source.start();
      this.setState("speaking");
    } catch (err) {
      console.info("[ai-guidance] voice: greeting clip not played", err);
      this.clipPlaying = false;
    }
  }

  /**
   * Oprește clipul: la barge-in (vizitatorul a vorbit peste), când providerul
   * începe să vorbească el, la Stop, sau natural, la sfârșitul lui. Dacă
   * conversația e încă vie și nimic altceva nu se aude, orbul revine pe
   * „ascultă".
   */
  private stopGreetingClip(interrupted: boolean): void {
    const source = this.clipSource;
    const wasPlaying = this.clipPlaying;
    this.clipSource = null;
    this.clipAnalyser = null;
    this.clipLevelBuf = null;
    this.clipPlaying = false;
    if (source && interrupted) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        // Deja oprit.
      }
    }
    if (wasPlaying && !interrupted && this.provider && this.state === "speaking") this.setState("listening");
  }

  /** Nivelul de redare al clipului, 0..1, pentru orb. */
  private clipLevel(): number {
    const analyser = this.clipAnalyser;
    const buf = this.clipLevelBuf;
    if (!analyser || !buf) return 0;
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i]! - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / buf.length) * 3);
  }

  /**
   * Partea comună dintre pornirea manuală și reconectarea de după reload: un
   * provider viu, orbul, afișajul și bătaia de poll. Sesiunea de la backend e
   * deja deschisă (`this.token` setat) în ambele cazuri.
   *
   * `suppressGreeting` trece mai departe la provider doar pe calea de
   * reconectare - vezi `VoiceProvider.start` din `types.ts` pentru povestea
   * salutului dublu.
   */
  private async attach(session: VoiceSessionResponse, opts?: { suppressGreeting?: boolean }): Promise<void> {
    const voiceToken = session.voiceToken;
    this.token = voiceToken;
    const module = await loadVoiceModule(this.opts.apiBase);
    // Un singur factory posibil azi - vezi `VoiceVariant`. `renewToken` /
    // `geminiToken` / `model` din `VoiceSessionInfo` (types.ts) rămân definite
    // pentru Gemini Live, dar nu se mai populează de-aici: SDK-ul nu mai cere
    // niciodată `provider: "gemini"`, deci backendul nu mai întoarce acele
    // câmpuri pe această cale.
    const info: VoiceSessionInfo = {
      voiceToken,
      ...(session.legRef ? { legRef: session.legRef } : {}),
      ...(session.shimToken ? { shimToken: session.shimToken } : {}),
      ...(session.signedUrl ? { signedUrl: session.signedUrl } : {}),
      // ElevenLabs: id-ul de voce rezolvat de backend din cheia trimisă. La
      // reconectare vine de pe sesiune, deci vocea supraviețuiește reload-ului.
      ...(session.voiceId ? { voiceId: session.voiceId } : {}),
      // Vine doar de la deschiderea unei sesiuni cu istoric; la reconectare
      // backendul nu-l întoarce, iar `suppressGreeting` l-ar bate oricum.
      ...(session.greeting ? { greeting: session.greeting } : {}),
    };
    this.provider = module.createElevenLabsVoice(info, {
      onState: (state) => {
        // Providerul vorbește sau lucrează la o replică: clipul de salut, dacă
        // încă se auzea, se oprește - două voci deodată ar fi exact ce evităm.
        if (state === "speaking" || state === "processing") this.stopGreetingClip(true);
        // „listening" venit de la provider cât clipul încă se aude nu stinge
        // orbul: salutul E replica agentului, chiar dacă vine din browser.
        if (state === "listening" && this.clipPlaying) return;
        this.setState(state);
      },
      onTranscript: (role, text) => {
        if (role === "user") this.stopGreetingClip(true);
        this.noteTranscript(role, text);
        this.opts.onCaption(role, text);
      },
      // Timpul până la primul sunet nu se afișează vizitatorului - e o măsură
      // pentru noi. Ajunge doar în consolă.
      onTimeToFirstAudio: (ms) => {
        console.info("[ai-guidance] voice: first audio after", ms, "ms");
      },
      onError: (message) => this.opts.onError(message),
      // Conexiunea a murit fără să fi cerut noi stop (rețea, inactivitate,
      // agentul a închis). Fără demontarea asta, `active` rămâne adevărat pe o
      // conversație moartă și tap-ul următor nimerește ramura Stop: vizitatorul
      // apasă de două ori ca să repornească - adică „nu se mai activează vocea"
      // (raportat pe mobil, 11 aug 2026). Două excepții, ambele cu demontare
      // deja în curs sau interzisă:
      //   - `stop()` a golit deja `this.provider` înainte să închidă conexiunea;
      //   - markerul de reluare e scris → documentul se descarcă chiar acum
      //     pentru o navigare, iar sesiunea de SERVER trebuie să supraviețuiască
      //     reload-ului, nu să fie închisă aici.
      onEnded: () => {
        if (!this.provider) return;
        try {
          if (sessionStorage.getItem(RESUME_KEY)) return;
        } catch {
          // Fără storage nu există nici reluare - deci demontăm.
        }
        // Cădere fără navigare (rețea, providerul închide pe liniște - văzut pe
        // 12 aug 2026: 10s de tăcere totală și conversația moartă): o singură
        // încercare tăcută de reconectare la ACEEAȘI sesiune înainte de stop.
        // Același lacăt ca `start()`/`resumeAfterReload()`: cât reconectarea e pe
      // fir, un tap pe microfon sau o reluare după reload așteaptă, în loc să
      // deschidă a doua conversație peste prima.
      void this.runExclusive(() => this.recoverDroppedConnection());
      },
    });

    this.opts.presence.show();
    await this.provider.start(opts);
    // Navigarea făcută de OM (click pe un link, „View cart") descarcă documentul
    // fără să treacă prin executor - markerul se scrie aici, la `pagehide`, ca
    // sesiunea să supraviețuiască oricărui salt, nu doar celor comandate de
    // agent. `markResumeAcrossReload` nu scrie nimic după stop (provider null),
    // deci un stop urmat de navigare nu reînvie microfonul.
    if (!this.onPageHide) {
      this.onPageHide = () => this.markResumeAcrossReload("human");
      window.addEventListener("pagehide", this.onPageHide);
    }
    this.startLevelLoop();
    // O singură bătaie, oricâte porniri: un interval rămas de la instanța
    // anterioară ar consuma acțiunile în paralel cu cel nou.
    if (this.poll) clearInterval(this.poll);
    this.poll = window.setInterval(() => void this.pollState(), POLL_MS);
  }

  /**
   * Ultima fereastră dinaintea unei navigări care descarcă documentul: lăsăm în
   * `sessionStorage` tokenul sesiunii, ca pagina nouă să se poată reconecta la
   * EA, nu să deschidă alta. Fără conversație vocală activă nu scriem nimic -
   * altfel un salt din chatul scris ar porni microfonul pe pagina următoare.
   *
   * Două căi o cheamă, pentru două feluri de salt:
   * - executorul, chiar înainte de `location.assign` al unei acțiuni de agent;
   * - `pagehide` (armat în `attach`), pentru navigarea făcută de OM - vizitatorul
   *   care apasă „View cart" în mijlocul conversației (16:20, 12 aug 2026, pe
   *   staging: fără marker, deconectarea arăta ca una terminală, `onEnded` dădea
   *   stop, iar sesiunea murea exact când omul se ducea unde îl trimisese
   *   agentul). O conversație vie acum o secundă NU e microfon nesolicitat -
   *   TTL-ul de 60s rămâne garda pentru tabul redeschis târziu.
   */
  markResumeAcrossReload(reason: ResumeReason = "agent"): void {
    if (!this.provider || !this.token) return;
    try {
      // Un salt de agent trece TOT prin `pagehide`, la o clipă după ce
      // executorul a scris markerul lui: ascultătorul de `pagehide` nu are voie
      // să-l rescrie ca „human", altfel timpul doi nu mai vine niciodată.
      if (reason === "human" && sessionStorage.getItem(RESUME_KEY)) return;
      sessionStorage.setItem(RESUME_KEY, JSON.stringify({ token: this.token, at: Date.now(), reason } satisfies ResumeMarker));
    } catch {
      // Fără storage nu există reluare - conversația se oprește la reload, ca
      // înainte. Nu e un motiv să blocăm navigarea.
    }
  }

  /**
   * Pe pagina NOUĂ, la init. Dacă tura precedentă a navigat cu reload, reluăm
   * exact aceeași sesiune de server pe o conexiune nouă la provider și intrăm
   * direct în „timpul doi": ecranul nou pe sesiune, apoi sentinelul. Vizitatorul
   * aude o singură propoziție, la trecut, cu analiza gata.
   *
   * Întoarce `true` doar dacă vocea chiar a repornit - apelantul își aliniază
   * butonul de microfon după asta.
   */
  async resumeAfterReload(): Promise<boolean> {
    // Markerul se consumă ÎNTÂI și necondiționat: orice ieșire de mai jos
    // (sesiune expirată, micul refuzat, rețea) trebuie să lase pagina curată.
    const marker = takeVoiceResume();
    if (!marker || this.provider) return false;
    // Reluarea intră în același zbor ca pornirea manuală: un tap pe microfon
    // cât reconectarea e pe fir așteaptă în `start()` și găsește providerul
    // deja viu - nu mai deschide a doua conversație peste prima.
    const run = this.doResume(marker);
    this.inFlight = run.catch(() => {});
    try {
      return await run;
    } finally {
      this.inFlight = null;
    }
  }

  /**
   * Cere backendului o conexiune nouă la ACEEAȘI sesiune de server. 404 =
   * sesiunea a expirat sau procesul s-a repornit - nu o eroare de arătat
   * vizitatorului: pur și simplu nu mai e nimic de continuat.
   */
  private async mintReconnect(token: string): Promise<VoiceSessionResponse> {
    const { appId, publicKey, apiBase } = this.opts;
    const response = await fetch(`${apiBase}/v1/apps/${appId}/voice/session/${token}/reconnect`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-public-key": publicKey },
      // Bilanțul audio al leg-ului care tocmai s-a închis (F-08). Peste un
      // reload contorul e oricum gol - pagina nouă n-a auzit nimic.
      body: JSON.stringify({ legStats: this.takeLegStats() }),
    });
    if (!response.ok) throw new Error(`reconnect_failed_${response.status}`);
    return (await response.json()) as VoiceSessionResponse;
  }

  private async doResume(marker: ResumeMarker): Promise<boolean> {
    let session: VoiceSessionResponse;
    try {
      session = await this.mintReconnect(marker.token);
    } catch (err) {
      this.token = null;
      this.provider = null;
      // Discret, în consolă: sesiunea nu mai există, deci n-are ce continua,
      // iar butonul de microfon rămâne acolo, funcțional pentru un start curat.
      console.info("[ai-guidance] voice: could not resume after navigation", err);
      this.reportResume("voice_resume_failed");
      return false;
    }
    try {
      // Suprimăm salutul: conversația la provider e nouă, dar sesiunea de la
      // noi (și vizitatorul) sunt deja la mijlocul turei - vezi types.ts.
      await this.attach({ ...session, voiceToken: marker.token }, { suppressGreeting: true });
    } catch (err) {
      // Sesiunea de server TRĂIEȘTE, doar conexiunea audio n-a putut porni.
      // Cazul concret: Safari refuză `getUserMedia` fără un gest de utilizator,
      // iar pagina abia s-a încărcat - niciun gest încă. Nimic pe jumătate
      // pornit (`active` ar minți), dar tokenul rămâne: următorul tap pe
      // microfon E gestul care lipsea, iar `doStart` reia exact sesiunea asta
      // în loc să deschidă alta. Suprafața e anunțată ca vizitatorul să știe
      // că are ce relua - altfel „conversația s-a închis singură" (F-03).
      this.token = null;
      this.provider = null;
      this.pendingResume = { token: marker.token, reason: marker.reason };
      this.opts.onResumeBlocked?.();
      console.info("[ai-guidance] voice: resume blocked, will retry on the next mic tap", err);
      this.reportResume("voice_resume_blocked");
      return false;
    }
    void this.afterResume(marker.reason);
    return true;
  }

  /**
   * Reluarea n-a ajuns să vorbească: spunem serverului CARE ramură a căzut
   * (F-07, 21 sep 2026). Până acum serverul vedea doar `/reconnect` reușit și
   * apoi liniște, iar de ce a rămas vizitatorul fără voce nu se putea afla.
   * Fără conținut, fără așteptare - un semnal de diagnoză.
   */
  private reportResume(event: "voice_resume_blocked" | "voice_resume_failed"): void {
    const { appId, publicKey, apiBase, sessionId } = this.opts;
    try {
      void fetch(`${apiBase}/v1/apps/${appId}/event`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-public-key": publicKey },
        body: JSON.stringify({ event, sessionId, t: Date.now() }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Fără fetch (mediu de test vechi): diagnoza e opțională.
    }
  }

  /**
   * Ce urmează după o reconectare reușită pe pagina nouă, după cine a sărit:
   *   - salt de agent: exact continuarea existentă - randare → ecranul nou pe
   *     sesiune → sentinel. Backendul reia întrebarea originală (`lastQuestion`)
   *     peste snapshotul nou, deci răspunsul vine deja la trecut;
   *   - salt de om: doar ecranul nou pe sesiune, ca următoarea replică a
   *     vizitatorului să vadă pagina pe care e. Nicio tură nouă - omul n-a
   *     întrebat nimic, doar a dat click.
   */
  private async afterResume(reason: ResumeReason): Promise<void> {
    if (reason === "agent") {
      await this.resumeAfterNavigation();
      return;
    }
    await domSettled({ firstMutationMs: 250, quietMs: 250, maxMs: 1500 });
    await this.pushPage();
  }

  /** Ultima reconectare automată - plafonul care oprește o buclă de reconectări. */
  private lastAutoReconnectAt = 0;

  /**
   * Conexiunea la provider a murit fără stop cerut și fără navigare în curs.
   * O SINGURĂ încercare tăcută de reconectare la aceeași sesiune de server
   * (conversația, istoricul și ecranul trăiesc acolo, nu în conexiunea audio);
   * fără salut, fără continuare - agentul doar ascultă din nou, iar vizitatorul
   * nu observă nimic. A doua cădere în interiorul plafonului înseamnă o cauză
   * persistentă (rețea, cotă provider) - acolo demontăm curat, ca până acum.
   */
  private async recoverDroppedConnection(): Promise<void> {
    const token = this.token;
    const now = Date.now();
    if (!token || now - this.lastAutoReconnectAt < AUTO_RECONNECT_COOLDOWN_MS) {
      void this.stop();
      return;
    }
    this.lastAutoReconnectAt = now;
    // Bătaia de poll a conexiunii MOARTE se oprește ÎNAINTE de orice await
    // (13 aug 2026, măsurat pe staging). Altfel intervalul de 500 ms continuă
    // în timpul reconectării, poate primi o acțiune de navigare a turei
    // dinainte, iar executorul scrie markerul de reluare și descarcă pagina
    // exact peste handshake-ul reconectării în curs: prima cerere moare fără
    // să deschidă vreo conversație, pagina nouă cere a doua. Așa au ieșit două
    // conectări la 0,7 s una de alta, dintre care una plătită degeaba.
    if (this.poll) {
      clearInterval(this.poll);
      this.poll = 0;
    }
    // Conexiunea veche e moartă; nulificat ca `attach` să construiască una nouă
    // și ca un `onEnded` întârziat al ei să nu mai aibă ce demonta.
    this.provider = null;
    try {
      const session = await this.mintReconnect(token);
      await this.attach({ ...session, voiceToken: token }, { suppressGreeting: true });
      console.info("[ai-guidance] voice: reconnected after a dropped connection");
    } catch (err) {
      console.info("[ai-guidance] voice: could not recover the dropped connection", err);
      void this.stop();
    }
  }

  async stop(): Promise<void> {
    // Stop apăsat cât pornirea încă zboară: fără așteptarea asta, `provider` e
    // încă null, stop-ul nu face nimic, iar pornirea se termină o clipă mai
    // târziu cu vocea în aer - un „Stop" care nu oprește nimic. Aceeași buclă
    // inline ca în `start` (vezi acolo de ce nu e un helper).
    while (this.inFlight) await this.inFlight.catch(() => {});
    if (this.poll) {
      clearInterval(this.poll);
      this.poll = 0;
    }
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    if (this.onPageHide) {
      window.removeEventListener("pagehide", this.onPageHide);
      this.onPageHide = undefined;
    }
    this.stopGreetingClip(true);
    const provider = this.provider;
    this.provider = null;
    await provider?.stop().catch(() => {});
    this.opts.presence.setState("idle");
    // Stratul de prezență se retrage doar dacă e un STRAT - peste panoul clasic
    // e decorul modului de voce, deci n-are ce căuta pe ecran după Stop. În
    // orb-chat orbul E suprafața de conversație, iar `hide()` acolo ar lăsa
    // vizitatorul fără nimic pe ecran și fără nimic de apăsat: `Presence` știe
    // singur în care dintre cele două cazuri e (vezi `alwaysVisible`).
    this.opts.presence.hide();
    this.setState("idle");

    // Închiderea sesiunii la backend e best-effort: dacă pică, sesiunea expiră
    // singură. Nu merită un mesaj de eroare pentru vizitator.
    const token = this.token;
    this.token = null;
    if (token) {
      void fetch(`${this.opts.apiBase}/v1/apps/${this.opts.appId}/voice/session/${token}/end`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-public-key": this.opts.publicKey },
        body: JSON.stringify({ legStats: this.takeLegStats() }),
        // Vizitatorul poate închide tabul exact acum; keepalive dă cererii o
        // șansă să plece oricum.
        keepalive: true,
      }).catch(() => {});
    }
  }

  private setState(state: VoiceState): void {
    const wasSpeaking = this.state === "speaking";
    this.state = state;
    if (state === "speaking") {
      this.speakingSeen = true;
      // Redarea a revenit în răgaz: golul era între segmente, replica continuă.
      clearTimeout(this.speechEndGrace);
    }
    // Ieșirea din „speaking" e semnalul de sfârșit de replică pe care îl aștepta
    // saltul întârziat. Pe ElevenLabs vine din `onModeChange` (speaking →
    // listening) - dar abia după răgazul de mai sus, ca un gol între segmente să
    // nu taie replica (vezi `SPEECH_END_GRACE_MS`). `stop()`/deconectarea îl
    // aduce prin „idle", instant - deci nimeni nu rămâne blocat dacă vizitatorul
    // închide conversația la mijloc.
    if (state === "idle") {
      clearTimeout(this.speechEndGrace);
      this.flushSpeechEndWaiters();
    } else if (wasSpeaking && state !== "speaking") {
      clearTimeout(this.speechEndGrace);
      this.speechEndGrace = window.setTimeout(() => this.flushSpeechEndWaiters(), SPEECH_END_GRACE_MS);
    }
    this.opts.presence.setState(state);
    this.opts.onStateChange?.(state);
  }

  /**
   * Ține minte dacă tura curentă chiar are cuvinte. Tura MUTĂ de navigare
   * trimite spre sinteză un singur spațiu (`agentStream.ts`), deci `trim()` e
   * exact linia dintre „a vorbit" și „a tăcut". Replica vizitatorului începe o
   * tură nouă și resetează contorul.
   */
  private noteTranscript(role: "user" | "agent", text: string): void {
    if (role === "user") {
      this.spokeThisTurn = false;
      this.speakingSeen = false;
      return;
    }
    const spoken = text.trim();
    if (spoken && spoken !== VOICE_CONTINUE_SENTINEL) this.spokeThisTurn = true;
  }

  private flushSpeechEndWaiters(): void {
    const waiters = [...this.speechEndWaiters];
    this.speechEndWaiters.clear();
    for (const resolve of waiters) resolve();
  }

  /**
   * Replica turei curente e încă „în aer": ori se aude acum, ori are cuvinte
   * dar redarea n-a pornit încă. În ambele cazuri o navigare ar tăia cuvântul.
   * Dacă modelul a tăcut (tura mută de navigare) sau replica s-a terminat deja
   * (`speakingSeen` fără „speaking" curent), întoarce `false` - iar saltul
   * rămâne instantaneu, exact ca până acum.
   *
   * `serverSpoke` e semnalul autoritar de pe poll: serverul ȘTIE dacă tura care
   * a produs acțiunile avea text rostit, fără cursa de timing de aici.
   * Transcriptul providerului și primul „speaking" sosesc la secunde DUPĂ ce
   * acțiunile ajung pe sesiune (stream-ul LLM se termină înaintea sintezei), iar
   * poll-ul de 500ms prindea fereastra aia: `spokeThisTurn` încă fals, stare
   * încă „processing" → navigare imediată, care descărca documentul la
   * jumătatea replicii (raportat 12 aug 2026, pagina de retururi). Euristica
   * locală rămâne ca plasă pentru un răspuns de poll fără câmpul `spoke`.
   */
  private speechPending(serverSpoke: boolean): boolean {
    if (this.state === "speaking") return true;
    return (serverSpoke || this.spokeThisTurn) && !this.speakingSeen;
  }

  /**
   * Așteaptă sfârșitul replicii, cu plafon dur. Fără plafon, un semnal de stare
   * pierdut ar lăsa vizitatorul definitiv pe pagina veche - un eșec mult mai
   * urât decât un cuvânt tăiat.
   */
  /** Închiderea de cotă e deja în curs - flagul `end` de pe poll nu se re-execută. */
  private quotaEnding = false;

  /**
   * Închiderea comandată de server la epuizarea minutelor (F-06): întâi
   * sfârșitul replicii (rămas-bunul trebuie auzit întreg), apoi stop-ul curat
   * și nota în suprafață. `stop()` oprește și poll-ul, deci flagul nu revine.
   */
  private async endForQuota(): Promise<void> {
    await this.awaitSpeechEnd();
    await this.stop();
    this.opts.onQuotaEnded?.();
  }

  private awaitSpeechEnd(): Promise<void> {
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        this.speechEndWaiters.delete(finish);
        resolve();
      };
      const timer = setTimeout(finish, SPEECH_END_TIMEOUT_MS);
      this.speechEndWaiters.add(finish);
    });
  }

  /**
   * Nivelul audio real, împins în orb la fiecare cadru. Sursa se schimbă cu
   * starea: cât ascultăm, orbul respiră pe microfon; cât vorbește, pe redare.
   * Fără comutarea asta orbul ar pulsa pe propria voce în timp ce vizitatorul
   * vorbește - exact invers decât trebuie.
   */
  private startLevelLoop(): void {
    const tick = (): void => {
      const provider = this.provider;
      if (!provider) return;
      const level = this.clipPlaying
        ? this.clipLevel()
        : this.state === "speaking"
          ? provider.outputLevel()
          : provider.inputLevel();
      if (this.state === "speaking" && !this.clipPlaying) {
        this.legStats.samples += 1;
        this.legStats.sum += level;
        if (level > this.legStats.max) this.legStats.max = level;
      }
      this.opts.presence.feedLevel(level);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /**
   * Nivelul de redare al LEG-ULUI curent, cât agentul vorbește (F-08, 21 sep
   * 2026): „vocea și volumul s-au schimbat după reconectare" s-a raportat de
   * trei ori într-o sesiune, toate leg-urile cu aceeași voce. Cifrele pleacă
   * la server la închiderea leg-ului (`/end`, `/reconnect`), ca leg 1 și leg 2
   * ale aceleiași sesiuni să se poată compara înainte să atingem ceva.
   */
  private legStats = { samples: 0, sum: 0, max: 0 };

  private takeLegStats(): { samples: number; outputMean: number; outputMax: number } {
    const { samples, sum, max } = this.legStats;
    this.legStats = { samples: 0, sum: 0, max: 0 };
    return { samples, outputMean: samples ? Math.round((sum / samples) * 1000) / 1000 : 0, outputMax: Math.round(max * 1000) / 1000 };
  }

  /**
   * Împrospătează ecranul de pe sesiune după o MUTAȚIE fără navigare (add/remove
   * în coș). Fără asta, poll-ul urcă snapshotul doar la schimbarea de cale, iar
   * modelul „verifica" coșul pe date stătute: 12 aug 2026, staging - după o
   * re-adăugare greșită, agentul susținea senin „avem un singur trimmer în coș"
   * (snapshotul lui) în timp ce coșul real avea două.
   */
  refreshSessionPage(): void {
    if (!this.token) return;
    void this.pushPage();
  }

  /**
   * Urcă pe sesiune pagina curentă + snapshotul viu. Perechea vocală a
   * snapshotului din `/ask`: apelul LLM vine de la provider, server-to-server,
   * deci browserul trebuie să lase ecranul pe sesiune ÎNAINTE de tură.
   */
  private async pushPage(): Promise<void> {
    if (!this.token) return;
    const page = await this.opts.capturePage();
    this.pushedPath = page.path;
    await fetch(`${this.opts.apiBase}/v1/apps/${this.opts.appId}/voice/session/${this.token}/page`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-public-key": this.opts.publicKey },
      body: JSON.stringify(page),
    }).catch(() => {});
  }

  /**
   * Al doilea pas al unei ture care a început cu o navigare.
   *
   * Fără el, agentul anunța „deschid pipeline-ul", pagina se schimba - și acolo
   * se oprea: acțiunile aceleiași ture fuseseră deja consumate, iar ecranul nou
   * nu ajunsese niciodată la model, deci nu avea ce arăta (8 aug 2026, raportat
   * de Radu ca „se blochează la primul pas"). Nu se poate rezolva în aceeași
   * tură: `idx`-urile aparțin paginii vechi, iar configul scanat n-are elemente
   * cu care să țintim altfel.
   *
   * Deci: așteptăm randarea, urcăm ecranul nou pe sesiune și cerem providerului
   * o replică nouă. Vizitatorul aude o singură conversație continuă.
   */
  private async resumeAfterNavigation(): Promise<void> {
    if (this.resuming || !this.provider) return;
    this.resuming = true;
    try {
      // Orbul rămâne „pe gânduri" cât lucrăm - altfel ar arăta că ascultă, iar
      // vizitatorul ar începe să vorbească peste replica ce urmează.
      this.setState("processing");
      // Plafoane mai strânse decât la spotlight: aici fiecare milisecundă e
      // tăcere într-o conversație. Dacă routerul a randat deja sincron, ieșim
      // după `firstMutationMs`, nu după plafonul dur.
      await domSettled({ firstMutationMs: 250, quietMs: 250, maxMs: 1500 });
      await this.pushPage();
      this.provider?.resumeTurn(VOICE_CONTINUE_SENTINEL);
    } finally {
      this.resuming = false;
    }
  }

  /**
   * Execută acțiunile turei, cu o singură nuanță față de chatul scris: dacă
   * tura are ȘI cuvinte, ȘI o acțiune care mută pagina, saltul așteaptă sfârșitul
   * replicii.
   *
   * Promptul cere în continuare tura mută pentru mutările mecanice (vezi
   * `buildVoiceSegment`), iar aceea rămâne instantanee - nimic de așteptat, deci
   * `speechPending()` e fals și codul de aici e transparent. Asta e plasa de
   * siguranță pentru cazul în care modelul alege totuși să spună ceva înainte de
   * mutare (reacția empatică la povestea vizitatorului): în loc de un cuvânt
   * retezat de încărcarea paginii, vizitatorul aude propoziția întreagă și ABIA
   * apoi se mută. Bug live, 9 aug 2026, pe magazinul demo.
   *
   * Ce NU se amână: acțiunile care nu mută pagina (spotlight, carduri) din
   * aceeași tură - alea trebuie să se vadă CÂT vorbește, nu după.
   */
  private async runTurnActions(actions: SdkAction[], serverSpoke: boolean): Promise<void> {
    const moveAt = actions.findIndex((a) => PAGE_CHANGING_ACTIONS.has(a.type));
    if (moveAt < 0 || !this.speechPending(serverSpoke)) {
      await this.opts.runActions(actions);
    } else {
      if (moveAt > 0) await this.opts.runActions(actions.slice(0, moveAt));
      await this.awaitSpeechEnd();
      await this.opts.runActions(actions.slice(moveAt));
    }
    // Acțiunile turei s-au consumat: ce urmează e o tură nouă (continuarea de
    // după navigare vine cu propriul transcript).
    this.spokeThisTurn = false;
    this.speakingSeen = false;
  }

  private async pollState(): Promise<void> {
    if (!this.token) return;
    // Vizitatorul navighează în timp ce vorbește (deseori trimis chiar de un
    // spotlight): la fiecare bătaie verificăm dacă pagina s-a schimbat și, dacă
    // da, urcăm ecranul cel nou - tura următoare trebuie să vadă unde a AJUNS,
    // nu unde a început conversația.
    // Cât timp reluăm o tură, `resumeAfterNavigation` urcă el ecranul la
    // momentul potrivit (după randare) - un push de-aici ar trimite pagina pe
    // jumătate randată și l-ar dubla degeaba.
    if (!this.resuming && currentPagePath() !== this.pushedPath) await this.pushPage();
    try {
      const response = await fetch(
        `${this.opts.apiBase}/v1/apps/${this.opts.appId}/voice/session/${this.token}?path=${encodeURIComponent(currentPagePath())}`,
        { headers: { "x-public-key": this.opts.publicKey } },
      );
      // Sesiunea NU mai există pe server (restart de backend la un deploy -
      // sesiunile vii sunt în memoria procesului; văzut pe 12 aug 2026, când
      // poll-ul a bătut în 404 la nesfârșit peste o conversație-zombi în care
      // agentul nu mai putea răspunde). Câteva 404 la rând = demontare curată;
      // pragul absoarbe un 404 tranzitoriu al unui proxy.
      if (response.status === 404) {
        this.gonePolls += 1;
        if (this.gonePolls >= SESSION_GONE_POLLS) void this.stop();
        return;
      }
      this.gonePolls = 0;
      if (!response.ok) return;
      // `llmCostMicroUsd` și `turns` vin în continuare pe răspuns și se
      // contorizează server-side (usage records, la închiderea sesiunii) -
      // clientul nu le mai afișează de pe 10 aug 2026, deci nici nu le citește.
      // `spoke` spune dacă tura care a lăsat acțiunile avea și cuvinte - vezi
      // `speechPending` pentru de ce nu ne bazăm doar pe ce am auzit local.
      const state = (await response.json()) as {
        actions?: SdkAction[];
        spoke?: boolean;
        actionsBatchId?: string;
        end?: boolean;
      };
      // Serverul a închis conversația (minutele de voce s-au terminat, F-06):
      // lăsăm rămas-bunul să se termine de rostit, apoi închidem curat și
      // anunțăm suprafața - conversația continuă în scris, cu transcriptul
      // deja în chat. O singură dată, oricâte poll-uri mai aduc flagul.
      if (state.end && !this.quotaEnding) {
        this.quotaEnding = true;
        void this.endForQuota();
        return;
      }
      if (!state.actions?.length) return;
      // Dedup idempotent pe lot (12 aug 2026): după un restart de backend,
      // sesiunea rehidratată poate RELIVRA outbox-ul - un lot deja executat
      // aici nu se mai execută a doua oară (dubla adăugare în coș, dublul salt).
      if (state.actionsBatchId) {
        if (this.executedBatches.has(state.actionsBatchId)) return;
        this.executedBatches.add(state.actionsBatchId);
      }

      const before = currentPagePath();
      await this.runTurnActions(state.actions, state.spoke === true);
      // Dacă acțiunile chiar ne-au MUTAT pe altă pagină, tura nu s-a terminat:
      // ecranul pe care agentul urma să arate ceva abia acum există. Condiția e
      // pe calea reală, nu pe „a existat o acțiune navigate": un navigate spre
      // pagina curentă e no-op, iar altfel s-ar relua tura la nesfârșit.
      if (currentPagePath() !== before) await this.resumeAfterNavigation();
    } catch {
      // O tură de poll ratată nu strică nimic: următoarea vine în jumătate de
      // secundă, iar acțiunile rămân pe sesiune până sunt citite.
    }
  }
}

/**
 * Ce răspunde backendul la deschiderea (și la reconectarea) unei sesiuni.
 * `voiceToken` - sesiunea NOASTRĂ - e comun oricărui provider. `geminiToken` /
 * `model` rămân în tip fiindcă backendul (dormant, per verdict) încă le poate
 * întoarce dacă e chemat direct cu `provider: "gemini"` - SDK-ul, de-aici
 * încolo, nu mai cere niciodată asta, deci pe calea reală ele nu apar.
 */
interface VoiceSessionResponse {
  voiceToken: string;
  /**
   * Salutul PREÎNREGISTRAT (docs/plan-salut-vocal-preinregistrat.md): hash-ul
   * clipului MP3 de pe backend. Prezent = redăm clipul din browser și deschidem
   * conversația cu salutul suprimat; `greeting` lipsește atunci.
   */
  greetingClipHash?: string;
  /**
   * Identitatea opacă a sesiunii, dată providerului ca `user_id` - firul prin
   * care minutele lui se reconciliază exact pe clientul nostru. Nu autorizează
   * nimic; opțional, ca un backend mai vechi să nu strice deschiderea.
   */
  /**
   * Identitatea opacă a acestei CONECTĂRI, dată providerului ca `user_id`. Per
   * conectare, nu per sesiune: fiecare reconectare deschide o conversație nouă
   * la ei, iar costul sesiunii e suma lor.
   */
  legRef?: string;
  /**
   * Credentialul pentru shim - SINGURUL care pleacă spre provider. Separat de
   * `voiceToken`, care rămâne al browserului: cel dat providerului ajunge în
   * datele lui și se poate citi înapoi din API-ul lor.
   */
  shimToken?: string;
  signedUrl?: string;
  /** ElevenLabs: id-ul de voce pentru override, ales de server din allow-list. */
  voiceId?: string;
  /**
   * Salutul de REVENIRE, compus pe server când sesiunea asta a mai vorbit cu
   * agentul. Absent pe o sesiune nouă și pe reconectare - vezi `VoiceSessionInfo`.
   */
  greeting?: string;
  geminiToken?: string;
  model?: string;
}

type VoiceModule = NonNullable<Window["__AIG_VOICE__"]>;

/** Încarcă `voice.js` o singură dată, chiar dacă se apasă de mai multe ori. */
let loading: Promise<VoiceModule> | null = null;

function loadVoiceModule(apiBase: string): Promise<VoiceModule> {
  if (window.__AIG_VOICE__) return Promise.resolve(window.__AIG_VOICE__);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${apiBase}/voice.js`;
    script.async = true;
    script.onload = () => {
      const module = window.__AIG_VOICE__;
      if (module) resolve(module);
      else reject(new Error("voice module loaded but exposed nothing"));
    };
    script.onerror = () => {
      // Resetăm, ca o a doua încercare (rețea revenită) să nu fie blocată de
      // promisiunea respinsă a primei.
      loading = null;
      reject(new Error("voice module failed to load"));
    };
    document.head.appendChild(script);
  });
  return loading;
}
